import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { IState } from 'models/reducers/i-state.interface';
import { IVipNextLevel, IVipPerks } from 'models/vip/i-vip-user.interface';

import { getVipUser } from 'services/vip-user.service';

import { queryKeys } from './query-keys';

export interface UseVipUserReturn {
  vipLevel: number | null;
  balance: number | null;
  nextLevel: IVipNextLevel | null;
  perks: IVipPerks | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

const fetchVipUser = async () => {
  const response = await getVipUser(new AbortController().signal);
  return response.data;
};

export const useVipUserQuery = (): UseVipUserReturn => {
  const walletAddress = useSelector((state: IState): string => state.wallet.address);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.vipUser.data(walletAddress),
    queryFn: fetchVipUser,
    enabled: !!walletAddress,
    staleTime: 30000,
    retry: 1,
  });

  return {
    vipLevel: data?.vip_level ?? null,
    balance: data?.balance ?? null,
    nextLevel: data?.next_level ?? null,
    perks: data?.perks ?? null,
    loading: isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch VIP user data') : null,
    refetch,
  };
};